// src/lib/rateLimit.js — header-driven write budget tracker (§4)
//
// Bluesky PDS write budget is points-based. Constants are FALLBACKS only —
// verify against current Bluesky rate-limit documentation at build time, and
// treat `ratelimit-*` response headers as authoritative at runtime.
// Self-hosted PDSs may differ — headers always win.

export const RATE_LIMIT_DEFAULTS = {
  // Fallback constants (plan §4: verify at build time)
  pointsPerHour: 5000,
  pointsPerDay: 35000,
  pointsCreate: 3,
  pointsDelete: 1,
  defaultPacingMs: 1000, // ~1s between orphans ≈ 3,600 pts/hr, comfortably under
  lowRemainingThreshold: 25,
};

/**
 * Parse ratelimit-* headers from a fetch/atproto response headers object.
 * @param {Headers|object} headers
 * @returns {{limit: number|null, remaining: number|null, resetEpochSec: number|null}}
 */
export function parseRateLimitHeaders(headers) {
  const get = (name) => {
    if (!headers) return null;

    if (headers.get !== undefined) return headers.get(name);
    // plain object fallback (e.g. mocks)
    const lower = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());

    return lower ? headers[lower] : null;
  };

  const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number.parseInt(v, 10);

    return Number.isFinite(n) ? n : null;
  };

  return {
    limit: num(get('ratelimit-limit')),
    remaining: num(get('ratelimit-remaining')),
    resetEpochSec: num(get('ratelimit-reset')),
  };
}

/**
 * Tracks the write budget across a cleanup run and decides when to pause.
 */
export class RateLimitTracker {
  /**
   * @param {Object} [opts]
   * @param {number} [opts.pacingMs]         - base delay between writes
   * @param {number} [opts.lowRemaining]     - back off when remaining <= this
   */
  constructor({ pacingMs = RATE_LIMIT_DEFAULTS.defaultPacingMs, lowRemaining = RATE_LIMIT_DEFAULTS.lowRemainingThreshold } = {}) {
    this.pacingMs = pacingMs;
    this.lowRemaining = lowRemaining;
    this.remaining = null;
    this.resetEpochSec = null;
    this.limit = null;
    this.writes = 0;
    this.onPause = null; // ({pausedForMs, remaining}) => void
  }

  /** Feed response headers after each write. */
  observe(headers) {
    const { limit, remaining, resetEpochSec } = parseRateLimitHeaders(headers);

    if (remaining !== null) this.remaining = remaining;

    if (resetEpochSec !== null) this.resetEpochSec = resetEpochSec;

    if (limit !== null) this.limit = limit;
    this.writes += 1;
  }

  /**
   * Wait as needed: pacing between writes, plus backoff when remaining is low
   * (or unknown after enough writes). Returns the ms actually waited.
   */
  async waitBeforeNextWrite() {
    // Pacing delay always applies between writes.
    let delay = this.pacingMs;

    const remaining = this.remaining;
    const resetEpochSec = this.resetEpochSec;

    if (remaining !== null && resetEpochSec !== null && remaining <= this.lowRemaining) {
      const nowSec = Date.now() / 1000;
      const untilReset = resetEpochSec - nowSec;

      if (untilReset > 0) {
        delay = Math.max(delay, (untilReset + 1) * 1000); // +1s buffer
        this.onPause?.({ pausedForMs: delay, remaining });
      }
    } else if (remaining !== null && remaining <= 0) {
      const nowSec = Date.now() / 1000;
      const untilReset = (resetEpochSec ?? nowSec + 3600) - nowSec;
      delay = Math.max(delay, (untilReset + 1) * 1000);
      this.onPause?.({ pausedForMs: delay, remaining });
    }

    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    return delay;
  }

  /** Reset tracking (e.g. at run start). */
  reset() {
    this.remaining = null;
    this.resetEpochSec = null;
    this.limit = null;
    this.writes = 0;
  }
}

export default RateLimitTracker;
